
import { documents, userData, staticDir as staticDir_1, cwd } from "./Bridge.js";
import { extname, normalise, dirname, basename, join } from "./PathHelpers.js";
import { dbg, error as error_1, warn } from "../Shared/Log.js";
import { replace, toText, interpolate, substring, printf, split, toFail, join as join_1, concat } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { Result_Map, Result_Bind, FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { exists as exists_2, toArray, toList, filter, isEmpty, tryItem, skip, reverse } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { isLetterOrDigit, isDigit } from "../fable_modules/fable-library-js.5.13.0/Char.js";
import { iterate, zip, item, head, tail, isEmpty as isEmpty_1, tryHead, fold, sortDescending, sortBy, exists as exists_1, length, filter as filter_1, ofArray, map, empty } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { StringModule_Contains, StringModule_EndsWith, StringModule_Concat, StringModule_SplitRemoveEmptyEntries, StringModule_Trim, StringModule_StartsWith, StringModule_TryParseWith, StringModule_SplitString, StringModule_ToLower } from "../Common/EEExtensions.js";
import { FSharpRef, Record, Union } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { class_type, bigint_type, record_type, int32_type, string_type, union_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { equals as equals_1, equalArrays, bigintHash, defaultOf, compareArrays } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { sortBy as sortBy_1, choose, tryFind, mapIndexed, item as item_1, equalsWith, singleton, allPairs, map as map_1, tryItem as tryItem_1 } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { tryParse } from "../fable_modules/fable-library-js.5.13.0/Int32.js";
import { compare as compare_1, create, utcNow, op_Subtraction } from "../fable_modules/fable-library-js.5.13.0/Date.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";
import { isEmpty as isEmpty_2, toArray as toArray_1, tryFind as tryFind_1, empty as empty_1, ofArray as ofArray_1 } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { equals, compare, fromOne, op_LeftShift, op_Subtraction as op_Subtraction_1, op_BitwiseAnd, fromInt32 } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { Convert_fromJson } from "../Common/SimpleJson/Json.Converter.js";
import { SimpleJson_parseNative } from "../Common/SimpleJson/SimpleJson.js";
import { createTypeInfo } from "../Common/SimpleJson/TypeInfo.Converter.js";
import { defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { PromiseBuilder__Delay_62FBFDE1, PromiseBuilder__Run_212F1D4B } from "../fable_modules/Fable.Promise.3.1.3/Promise.fs.js";
import { promise } from "../fable_modules/Fable.Promise.3.1.3/PromiseImpl.fs.js";
import { hexBignum, strToIntCheckWidth } from "../Simulator/NumberHelpers.js";
import { Array_groupBy } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { LoadedComponent_$reflection, LoadedComponent, getMemType, legacyTypesConvert, Component, ComponentType, InitMemData, LegacyCanvas_LegacyComponent, LegacyCanvas_LegacyConnection, SheetInfo, CCForm, Memory1 } from "../Common/CommonTypes.js";
import { ResultList_sequence, jsonCanvasHasOldIds, JsonHelpers_SavedInfo__get_getTimeStamp, JsonHelpers_SavedInfo__get_getSheetInfo, JsonHelpers_SavedInfo__get_getWaveInfo, JsonHelpers_jsonStringToState, sheetOfJson, JsonHelpers_stateToJsonStringExperimental, JsonHelpers_stateToJsonString } from "../Common/Helpers.js";
import { parseDiagramSignatureFor } from "../Simulator/CanvasExtractor.js";

export function getCWD(u) {
    return cwd;
}

/**
 * Absolute path to the static asset directory.
 * 
 * This used to be worked out here, three ways: __static in development, a bare
 * "./resources/static" relative to the working directory for production on Windows and Linux, and
 * __dirname/../../static for production on macOS. All three land on the same place as the main
 * process's process.resourcesPath, and __static in particular is a webpack substitution that
 * expands to an expression over `path` and `process` - so it could not survive contextIsolation
 * even in principle. Main resolves it once now and sends the answer; see Bridge.staticDirectory.
 */
export function staticDir() {
    return staticDir_1;
}

export const staticFileDirectory = staticDir_1;

export function pathJoin(args) {
    return join(args);
}

export function baseName(filePath) {
    return basename(filePath);
}

export function dirName(filePath) {
    return dirname(filePath);
}

/**
 * The same path written this platform's way: one kind of separator, no repeats, `.` and `..`
 * resolved. For comparing two paths that may have been written differently - a dialog and a
 * bridge call hand back backslashes on Windows, a path built by pathJoin or read out of
 * IssieSettings.json may have forward slashes, and as plain strings those are not equal.
 */
export function normalisePath(filePath) {
    return normalise(filePath);
}

export function readFile(filePath) {
    return window.issieBridge.fs.readFile(filePath);
}

/**
 * False rather than an exception for a path main will not let Issie see. Every caller treats this
 * as a question about whether to go on, and a throw here would turn a refusal into a crash - but it
 * is logged, because "the file is not there" and "you may not look" are different problems.
 */
export function exists(filePath) {
    try {
        return window.issieBridge.fs.exists(filePath);
    }
    catch (e) {
        warn(concat("exists \'", filePath, "\': ", e.message));
        return false;
    }
}

/**
 * True when the path exists and is a directory. False for a file, and for a path that is not
 * there at all, so it is safe to use as a filter before reading a directory's contents.
 */
export function isDirectory(filePath) {
    try {
        return window.issieBridge.fs.isDirectory(filePath);
    }
    catch (e) {
        warn(concat("isDirectory \'", filePath, "\': ", e.message));
        return false;
    }
}

export function extName(filePath) {
    return extname(filePath);
}

export function mkdir(folderPath) {
    window.issieBridge.fs.mkdir(folderPath);
}

export function readdir(folderPath) {
    return window.issieBridge.fs.readdir(folderPath);
}

export function unlink(folderPath) {
    window.issieBridge.fs.unlink(folderPath);
}

export function rename(oldPath, newPath) {
    window.issieBridge.fs.rename(oldPath,newPath);
}

export function ensureDirectory(dPath) {
    if (!exists(dPath)) {
        mkdir(dPath);
    }
}

/**
 * Modification time in milliseconds since the epoch, or None when the path is not there.
 * Works on a directory as well as a file. NB a directory's time changes when an entry is added
 * to it, removed from it or renamed - NOT when a file already in it is rewritten in place.
 */
export function modifiedTimeMs(filePath) {
    try {
        return window.issieBridge.fs.modifiedTimeMs(filePath);
    }
    catch (e) {
        warn(concat("modifiedTimeMs \'", filePath, "\': ", e.message));
        return undefined;
    }
}

/**
 * Make a directory if it is not there, saying why if that could not be done. Creating a
 * directory can genuinely fail - a read-only or full disk, a permissions policy - and the
 * callers here are all able to carry on without it.
 */
export function tryEnsureDirectory(dPath) {
    try {
        ensureDirectory(dPath);
        return exists(dPath) ? (new FSharpResult$2(/* Ok */ 0, [dPath])) : (new FSharpResult$2(/* Error */ 1, [concat("could not create the directory ", dPath)]));
    }
    catch (e) {
        return new FSharpResult$2(/* Error */ 1, [concat("could not create the directory ", dPath, ": ", e.message)]);
    }
}

/**
 * The per-user, writable Issie directory.
 * 
 * Anything Issie writes for the user - demo working copies, component libraries the user makes or
 * imports - belongs here and NOT beside the installation. On macOS the app bundle
 * is signed and notarised, so writing inside it invalidates the signature and Gatekeeper can
 * then refuse to launch it. On Windows the installation is usually under Program Files, which
 * needs administrator rights to write. Both fail only for installed users, never in a
 * development build, which is exactly the kind of bug that ships.
 */
export function tryUserDataDirectory() {
    try {
        return tryEnsureDirectory(userData);
    }
    catch (e) {
        return new FSharpResult$2(/* Error */ 1, [concat("could not find the user data directory: ", e.message)]);
    }
}

function tryUserSubdirectory(name) {
    return Result_Bind((root) => tryEnsureDirectory(pathJoin([root, name])), tryUserDataDirectory());
}

/**
 * Where a demo project is copied so that the user can edit it.
 */
export function tryUserDemosDirectory() {
    return tryUserSubdirectory("demos");
}

/**
 * Where libraries the user makes or imports live. The libraries shipped with Issie are NOT copied
 * here: they stay read-only under the installation, and are found there directly.
 */
export function tryUserLibrariesDirectory() {
    return tryUserSubdirectory("libraries");
}

/**
 * Show a directory in the platform's file manager.
 * 
 * On Windows this launches explorer.exe rather than calling shell.openPath, because the folder
 * window is created by the already-running explorer.exe, which has no right to take the
 * foreground away from Issie - so shell.openPath opens it *behind* the app. A process launched by
 * the foreground process does have that right and passes it on. Measured on Windows 11: through
 * shell.openPath the window lands immediately below Issie and Issie keeps focus, through the
 * spawn immediately above it and focused. Every other platform raises it already, and
 * shell.openPath is the portable route there.
 * 
 * onError is given a readable reason when the directory cannot be shown. On platforms other than
 * Windows it is called asynchronously, since shell.openPath reports failure by resolving with a
 * non-empty message rather than by rejecting - so saying nothing would make a failure look like
 * success.
 */
export function openFolderInFileManager(path, onError) {
    const pr = window.issieBridge.revealInFileManager(path);
    void (pr.then((error) => {
        if (error !== "") {
            onError(error);
        }
    }));
}

export function pathWithoutExtension(filePath) {
    let source_1;
    const ext = extName(filePath);
    return join_1("", reverse((source_1 = reverse(filePath.split("")), skip(ext.length, source_1))));
}

export const baseNameWithoutExtension = (arg) => baseName(pathWithoutExtension(arg));

export function fileNameIsBad(name) {
    let c;
    let matchValue;
    const option_1 = tryItem(0, name);
    matchValue = ((option_1 != null) ? ((c = option_1, isDigit(c) ? true : (c === "_"))) : undefined);
    let matchResult;
    if (matchValue == null) {
        matchResult = 1;
    }
    else if (matchValue) {
        matchResult = 0;
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return true;
        default:
            return !isEmpty(filter((ch) => {
                let ch_1;
                return !((ch === " ") ? true : ((ch_1 = ch, isLetterOrDigit(ch_1) ? true : (ch_1 === "_"))));
            }, name));
    }
}

export const filePathIsBad = (arg) => fileNameIsBad(baseNameWithoutExtension(arg).split(""));

export function fileExistsWithExtn(extn, folderPath, baseName_1) {
    return exists(pathJoin([folderPath, baseName_1 + extn]));
}

export function tryReadFileSync(fPath) {
    if (!exists(fPath)) {
        return new FSharpResult$2(/* Error */ 1, [concat("Error: file ", fPath, " does not exist")]);
    }
    else {
        return new FSharpResult$2(/* Ok */ 0, [readFile(fPath)]);
    }
}

/**
 * Write utf8 encoded data to file.
 * Create file if it does not exist.
 */
export function writeFile(path, data) {
    try {
        window.issieBridge.fs.writeFile(path,data);
        return new FSharpResult$2(/* Ok */ 0, [undefined]);
    }
    catch (e) {
        return new FSharpResult$2(/* Error */ 1, [`Error '${e.message}' writing file '${path}'`]);
    }
}

/**
 * read file names from directory: returning [] on any error.
 */
export function readFilesFromDirectory(path) {
    if (exists(path)) {
        try {
            return toList(readdir(path));
        }
        catch (e) {
            warn(concat("could not read the directory \'", path, "\': ", e.message));
            return empty();
        }
    }
    else {
        warn(concat("could not read the directory \'", path, "\': it does not exist"));
        return empty();
    }
}

function subdirectoryNamesOf(folderPath) {
    return window.issieBridge.fs.readdirDirectories(folderPath);
}

/**
 * The immediate subdirectories of a folder, as full paths. [] if it cannot be read.
 * 
 * Deliberately not readdir: under .NET that is Directory.GetFiles, which lists files only, while
 * node's readdirSync lists directories too. Anything looking for subfolders through readdir
 * therefore works in the app and finds nothing under test - which is how this function came to
 * exist.
 * 
 * The directories come from the one readdir rather than from a stat of each entry, which is what
 * this used to do: existsSync and lstatSync per name, paid on every FILE in the folder before
 * discarding it. Listing C:\Windows\System32 took 244ms that way and takes 3ms this way - 4,885
 * entries, of which 4,687 were files answering a question nobody asked. Directory.GetDirectories
 * never had the problem, so only the node side changes.
 */
export function readSubdirectories(folderPath) {
    if (!isDirectory(folderPath)) {
        return empty();
    }
    else {
        try {
            return map((name) => pathJoin([folderPath, name]), ofArray(subdirectoryNamesOf(folderPath)));
        }
        catch (e) {
            warn(concat("could not list the subdirectories of \'", folderPath, "\': ", e.message));
            return empty();
        }
    }
}

export function hasExtn(extn, fName) {
    return StringModule_ToLower(fName).endsWith(StringModule_ToLower(extn));
}

/**
 * copy a sheet from some source path to a destination path
 */
export function copyFile(sourcePath, newPath) {
    const matchValue = writeFile(newPath, readFile(sourcePath));
    if (matchValue.tag === 1) {
        error_1(matchValue.fields[0]);
    }
}

export function readFilesFromDirectoryWithExtn(path, extn) {
    return filter_1((name) => hasExtn(extn, name), readFilesFromDirectory(path));
}

/**
 * What a directory looks like to Issie.
 * 
 * The .dprj marker is not needed to load a project - loadAllComponentFiles reads the .dgm files
 * and never opens it - but it is how a project is told from a folder that merely happens to have
 * sheets in it, and how "would this new project be inside an existing one?" is answered.
 */
export class ProjectDirectory extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["IsProject", "SheetsButNoMarker", "IsLibrary", "NotAProject"];
    }
    static IsProject = new ProjectDirectory(0, []);
    static SheetsButNoMarker = new ProjectDirectory(1, []);
    static IsLibrary = new ProjectDirectory(2, []);
    static NotAProject = new ProjectDirectory(3, []);
}

export function ProjectDirectory_$reflection() {
    return union_type("FilesIO.ProjectDirectory", [], ProjectDirectory, () => [[], [], [], []]);
}

/**
 * Which combination of marker, sheets and components means what. The one place that rule lives:
 * inspectFolder reads a folder to answer it, and the project browser is handed the same facts by
 * main without either of them having to agree about the meaning separately.
 * 
 * Sheets win over components where a folder somehow holds both: .dgm is what a project is made of,
 * and a stray .ldgm beside them is a file somebody copied there rather than a library.
 */
export function classifyFolder(hasMarker, sheetCount, componentCount) {
    const matchValue = sheetCount > 0;
    const matchValue_1 = componentCount > 0;
    if (hasMarker) {
        return ProjectDirectory.IsProject;
    }
    else if (matchValue) {
        return ProjectDirectory.SheetsButNoMarker;
    }
    else if (matchValue_1) {
        return ProjectDirectory.IsLibrary;
    }
    else {
        return ProjectDirectory.NotAProject;
    }
}

/**
 * What a directory is to Issie, and how many things in it Issie can open - sheets for a project,
 * components for a library - from the one read of it. The count is free once the classification
 * has looked at the file names anyway.
 */
export function inspectFolder(path) {
    const files = readFilesFromDirectory(path);
    const sheets = length(filter_1((fName) => hasExtn(".dgm", fName), files)) | 0;
    const components = length(filter_1((fName_1) => hasExtn(".ldgm", fName_1), files)) | 0;
    const matchValue = classifyFolder(exists_1((fName_2) => hasExtn(".dprj", fName_2), files), sheets, components);
    if (matchValue.tag === 2) {
        return [ProjectDirectory.IsLibrary, components];
    }
    else {
        return [matchValue, sheets];
    }
}

export function inspectProjectDirectory(path) {
    return inspectFolder(path)[0];
}

/**
 * The empty file that marks a directory as an Issie project, named after the directory.
 */
export function projectMarkerPath(projectPath) {
    return pathJoin([projectPath, baseName(projectPath) + ".dprj"]);
}

/**
 * True when a path has no parent to go up to - a drive root, or the root of a share. dirName
 * returns such a path unchanged, which is what the browser's Up control asks.
 */
export function isFilesystemRoot(path) {
    return dirName(path) === path;
}

/**
 * One folder as the project browser draws it.
 */
export class FolderEntry extends Record {
    constructor(Path, Kind, SheetCount) {
        super();
        this.Path = Path;
        this.Kind = Kind;
        this.SheetCount = (SheetCount | 0);
    }
}

export function FolderEntry_$reflection() {
    return record_type("FilesIO.FolderEntry", [], FolderEntry, () => [["Path", string_type], ["Kind", ProjectDirectory_$reflection()], ["SheetCount", int32_type]]);
}

/**
 * Ask for a folder chosen in the project browser to be made readable, and say whether it was.
 * 
 * Every other read here goes through a channel confined to directories Issie already has a reason
 * to trust, and a folder just picked out of the browser is not yet one of them. Main decides, by
 * looking at the folder itself rather than taking the renderer's word: it admits one that holds
 * sheets or a project marker. False therefore means "no project there", not "refused".
 */
export function admitProjectFolder(path) {
    return window.issieBridge.browse.admitProject(path);
}

function forBrowsing(entries) {
    return sortBy((entry_1) => [(entry_1.Kind.tag === 3) ? 1 : 0, StringModule_ToLower(baseName(entry_1.Path))], filter_1((entry) => !baseName(entry.Path).startsWith("."), entries), {
        Compare: (x, y) => (compareArrays(x, y) | 0),
    });
}

/**
 * Every immediate subdirectory of `path`, classified, for the browser to draw - or why the folder
 * could not be listed. One level only: this lists a folder, it does not search a disk.
 * 
 * A native folder picker draws every folder alike, so it cannot show which of them hold projects -
 * which is the whole reason Issie draws this list itself.
 * 
 * Deliberately NOT readSubdirectories and inspectFolder, which reach the operating system through
 * the confined filesystem channel. The folder being browsed is by definition one the user has not
 * opened yet, so every folder worth showing was refused: the dialog opened on the folder holding
 * the last project and reported that the user's Documents did not exist. The browse channel in
 * src/Main/Bridge.fs answers this one question without that confinement, and carries names and
 * counts rather than the contents of anything.
 */
export function browseFolderForOpening(path) {
    const listing = window.issieBridge.browse.folder(path);
    if (!listing.exists) {
        return new FSharpResult$2(/* Error */ 1, ["That folder is not there."]);
    }
    else {
        return new FSharpResult$2(/* Ok */ 0, [forBrowsing(map((entry) => {
            const kind = classifyFolder(entry.hasMarker, entry.sheetCount, entry.componentCount);
            return new FolderEntry(entry.path, kind, (kind.tag === 2) ? entry.componentCount : entry.sheetCount);
        }, ofArray(listing.entries)))]);
    }
}

export function removeExtn(extn, fName) {
    if (hasExtn(extn, fName)) {
        return fName.slice(0, ((fName.length - extn.length) - 1) + 1);
    }
    else {
        return undefined;
    }
}

/**
 * returns the list of backup files in descending chronological order.
 */
export function backupFileData(path, baseName_1) {
    return sortDescending(map((fn_1) => {
        let option, binder, option_1;
        return [(option = tryItem_1(1, StringModule_SplitString(["-"], fn_1)), (binder = StringModule_TryParseWith((arg) => {
            let outArg = 0;
            return [tryParse(arg, 511, false, 32, new FSharpRef(() => (outArg | 0), (v) => {
                outArg = (v | 0);
            })), outArg];
        }), (option_1 = option, (option_1 != null) ? binder(option_1) : undefined))), fn_1];
    }, filter_1((fn) => StringModule_StartsWith(baseName_1 + "-", fn), readFilesFromDirectory(path))), {
        Compare: (x, y) => (compareArrays(x, y) | 0),
    });
}

/**
 * The backup directory of a project: where every sheet's automatic backups are kept.
 */
export function backupDirOf(projectPath) {
    return pathJoin([projectPath, "backup"]);
}

/**
 * Delete the backups in a project's backup directory that are older than `maxAgeHours`, and say
 * how many went. Nothing at all when there is no backup directory, which is the ordinary case for
 * a project nobody has edited.
 * 
 * Age is the file's modification time and not the date written into its name. The name is built
 * from ToShortDateString, whose format is the user's locale, so it cannot be parsed back; the
 * filesystem knows the answer already.
 * 
 * A backup exists to undo an accident that has just happened: the .dgm is the saved work, and the
 * backup is what the last few edits were before it. Two days on it is a file nobody will open,
 * and a term's worth of them is what a project directory silently fills with. Deliberately NOT
 * "keep the newest of each sheet" - a sheet untouched for a week is one whose saved .dgm is what
 * the user meant, and an old backup beside it only makes the folder harder to read.
 */
export function removeBackupsOlderThan(maxAgeHours, projectPath) {
    const backupDir = backupDirOf(projectPath);
    const matchValue = isDirectory(backupDir);
    if (matchValue) {
        const cutoff = op_Subtraction(utcNow(), create(1970, 1, 1)) - (((maxAgeHours * 60) * 60) * 1000);
        return fold((gone, path_1) => {
            try {
                unlink(path_1);
                return (gone + 1) | 0;
            }
            catch (e) {
                warn(concat("could not remove the old backup \'", path_1, "\': ", e.message));
                return gone | 0;
            }
        }, 0, filter_1((path) => {
            const matchValue_1 = modifiedTimeMs(path);
            if (matchValue_1 == null) {
                return false;
            }
            else {
                return matchValue_1 < cutoff;
            }
        }, map((name) => pathJoin([backupDir, name]), readFilesFromDirectory(backupDir)))) | 0;
    }
    else {
        return 0;
    }
}

/**
 * returns the sequence number and name of the most recent (highest sequence number) backup file
 */
export function latestBackupFileData(path, baseName_1) {
    const option_1 = tryHead(backupFileData(path, baseName_1));
    if (option_1 != null) {
        const _arg = option_1;
        if (_arg[0] != null) {
            return [_arg[0], _arg[1]];
        }
        else {
            return undefined;
        }
    }
    else {
        return undefined;
    }
}

export function makeData(aWidth, dWidth, makeFun) {
    const a = ~~(aWidth / 2) | 0;
    const inp = toArray(rangeDouble(0, 1, (1 << a) - 1));
    return ofArray_1(map_1((tupledArg) => {
        let n;
        const x = tupledArg[0] | 0;
        const y = tupledArg[1] | 0;
        return [fromInt32((x << a) + y), (n = makeFun(x, y), (dWidth === 64) ? n : op_BitwiseAnd(op_Subtraction_1(op_LeftShift(fromOne(), dWidth), fromOne()), n))];
    }, allPairs(inp, inp)), {
        Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
    });
}

export function makeFixedROM(addr, data, mem) {
    let a, a_1;
    const signExtend = (w, n) => {
        if ((n & (1 << (w - 1))) !== 0) {
            return (((-1 << w) | n) & -1) | 0;
        }
        else {
            return n | 0;
        }
    };
    const matchValue = mem.Init;
    let matchResult;
    switch (matchValue.tag) {
        case 4: {
            if ((a = (addr | 0), ((a % 2) === 0) && (a <= 16))) {
                matchResult = 0;
            }
            else {
                matchResult = 3;
            }
            break;
        }
        case 5: {
            if ((a_1 = (addr | 0), ((a_1 % 2) === 0) && (a_1 <= 16))) {
                matchResult = 1;
            }
            else {
                matchResult = 3;
            }
            break;
        }
        case 0: {
            matchResult = 2;
            break;
        }
        default:
            matchResult = 3;
    }
    switch (matchResult) {
        case 0: {
            const d_2 = data | 0;
            return new FSharpResult$2(/* Ok */ 0, [makeData(addr, d_2, (x, y) => fromInt32((x * y) % (1 << d_2)))]);
        }
        case 1: {
            const d_3 = data | 0;
            const a_3 = addr | 0;
            const w_1 = ~~(a_3 / 2) | 0;
            return new FSharpResult$2(/* Ok */ 0, [makeData(a_3, d_3, (x_1, y_1) => fromInt32((signExtend(w_1, x_1) * signExtend(w_1, y_1)) & ((1 << d_3) - 1)))]);
        }
        case 2:
            return new FSharpResult$2(/* Ok */ 0, [mem.Data]);
        default:
            return toFail(`addr=${addr}, data=${data}, int=${mem.Init} not allowed in makeFixedROM`);
    }
}

export function jsonStringToMem(jsonString) {
    try {
        return new FSharpResult$2(/* Ok */ 0, [Convert_fromJson(SimpleJson_parseNative(jsonString), createTypeInfo(class_type("Microsoft.FSharp.Collections.FSharpMap`2", [bigint_type, bigint_type])))]);
    }
    catch (ex) {
        return new FSharpResult$2(/* Error */ 1, [ex.message]);
    }
}

export function getBaseNameNoExtension(filePath) {
    const name = baseName(filePath);
    const matchValue = toList(split(name, ["."], undefined, 0));
    if (!isEmpty_1(matchValue)) {
        if (isEmpty_1(tail(matchValue))) {
            return head(matchValue);
        }
        else {
            return head(matchValue) + fold((baseName_1, i) => ((name + ".") + item(i, tail(matchValue))), "", toList(rangeDouble(0, 1, length(tail(matchValue)) - 2)));
        }
    }
    else {
        return toFail(printf("what? split at . in a filename should never return empty list"));
    }
}

function makeFileFilters(name, extn) {
    return singleton({
        name: name,
        extensions: [extn],
    });
}

/**
 * Ask the user to choose a project, with a dialog window.
 * Return the chosen folder, or None if the user exits without choosing one.
 * 
 * A project IS a directory, so that is what the dialog asks for. It used to ask for the .dprj
 * inside one, which meant navigating into the project, past its sheets greyed out by the filter,
 * to select a file that is empty, says nothing, and is named after the folder the user was
 * already standing in - and whose directory was then all that was kept. Asking for the folder
 * also lets a project whose marker was lost or renamed be opened, which the filter made
 * impossible even though loading a project never reads it.
 */
export function askForExistingProjectPath(defaultPath) {
    const options = {};
    options.title = "Choose an ISSIE project folder, or a folder containing projects";
    options.buttonLabel = "Open";
    options.properties = ["openDirectory"];
    options.defaultPath = defaultArg(defaultPath, documents);
    const _arg = ofArray(window.issieBridge.dialog.open(options));
    if (!isEmpty_1(_arg)) {
        return head(_arg);
    }
    else {
        return undefined;
    }
}

/**
 * Ask the user to choose a folder, for a caller that knows what it wants one for.
 * Return None if the user exits without choosing one.
 */
export function askForFolder(title, buttonLabel, defaultPath) {
    const options = {};
    options.title = title;
    options.buttonLabel = buttonLabel;
    options.properties = ["openDirectory", "createDirectory"];
    options.defaultPath = defaultArg(defaultPath, documents);
    const _arg = ofArray(window.issieBridge.dialog.open(options));
    if (!isEmpty_1(_arg)) {
        return head(_arg);
    }
    else {
        return undefined;
    }
}

/**
 * ask for existing sheet paths
 */
export function askForExistingSheetPaths(defaultPath) {
    let collection;
    const options = {};
    options.filters = ((collection = makeFileFilters("ISSIE sheet", "dgm"), Array.from(collection)));
    options.defaultPath = defaultArg(defaultPath, documents);
    options.properties = ["openFile", "multiSelections"];
    const _arg = ofArray(window.issieBridge.dialog.open(options));
    if (isEmpty_1(_arg)) {
        return undefined;
    }
    else {
        return _arg;
    }
}

export function askForExistingProjectPathAsync(_defaultPath) {
    return PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => ((window.__issieBrowserFiles.openProjectDirectory()).then((_arg) => {
        const result = _arg;
        return Promise.resolve((result == null) ? undefined : result);
    }))));
}

export function askForExistingSheetPathsAsync(_defaultPath) {
    return PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => ((window.__issieBrowserFiles.openSheetFiles()).then((_arg) => {
        const result = _arg;
        return Promise.resolve((result == null) ? undefined : ofArray(result));
    }))));
}

export function askForNewProjectPathAsync(_defaultPath) {
    return PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => ((window.__issieBrowserFiles.createProjectDirectory()).then((_arg) => {
        const result = _arg;
        return Promise.resolve((result == null) ? undefined : result);
    }))));
}

export function persistFileToExternalStorageAsync(filePath) {
    return PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => ((window.__issieBrowserFiles.persistFile(filePath)).then((_arg) => (Promise.resolve(undefined))))));
}

export function clearBrowserStoredPath(path) {
    window.__issieBrowserFiles.clearStoredPath(path);
}

export function loadBundledDemoAsync(sourcePath, targetPath) {
    return PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => ((window.__issieBrowserFiles.loadBundledDemo(sourcePath, targetPath)).then((_arg) => {
        const result = _arg;
        return Promise.resolve(!(result == null) && result);
    }))));
}

/**
 * Why a project may not be called this, if it may not.
 * 
 * One rule, in one place: the creation form asks it of every keystroke so that the user sees the
 * objection while they can still act on it, and tryCreateFolder asks it as the last word. It used
 * to be reachable only by breaking it, after the native dialog had been dismissed, in an error
 * box that took the typing with it.
 */
export function projectNameError(name) {
    if (name === "") {
        return "Enter a name for the project.";
    }
    else if (exists_2((arg) => {
        let ch_1;
        return !((ch_1 = arg, isLetterOrDigit(ch_1) ? true : (ch_1 === "_")));
    }, name.split(""))) {
        return "Project names must contain only letters, digits, or underscores. Spaces and hyphens are not allowed.";
    }
    else {
        return undefined;
    }
}

export function tryCreateFolder(path) {
    const matchValue = projectNameError(baseName(path));
    if (matchValue == null) {
        try {
            mkdir(path);
            return new FSharpResult$2(/* Ok */ 0, [undefined]);
        }
        catch (ex) {
            return new FSharpResult$2(/* Error */ 1, [concat("Can\'t create folder \'", path, "\': ", ex.message)]);
        }
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [matchValue]);
    }
}

/**
 * Asyncronously remove file.
 * ignore if file does not exist
 */
export function removeFileWithExtn(extn, folderPath, baseName_1) {
    const path = pathJoin([folderPath, baseName_1 + extn]);
    if (exists(path)) {
        try {
            unlink(path);
        }
        catch (matchValue) {
        }
    }
}

export function renameFile(extn, folderPath, baseName_1, newBaseName) {
    const oldPath = pathJoin([folderPath, baseName_1 + extn]);
    const newPath = pathJoin([folderPath, newBaseName + extn]);
    if (exists(oldPath)) {
        try {
            rename(oldPath, newPath);
            return new FSharpResult$2(/* Ok */ 0, [undefined]);
        }
        catch (e) {
            return new FSharpResult$2(/* Error */ 1, [`Rename of '${baseName_1}' in '${folderPath}' failed`]);
        }
    }
    else if (extn === ".dgm") {
        return new FSharpResult$2(/* Error */ 1, [`Error: The file '${baseName_1}${extn} appears to have been removed`]);
    }
    else {
        return new FSharpResult$2(/* Ok */ 0, [undefined]);
    }
}

export function removeFile(folderPath, baseName_1) {
    removeFileWithExtn(".dgm", folderPath, baseName_1);
}

export function removeAutoFile(folderPath, baseName_1) {
    unlink(pathJoin([folderPath, baseName_1 + ".dgmauto"]));
}

/**
 * Split a .ram file line into the part which defines a memory location and the comment written
 * against it, if any. A comment runs from "//" to the end of the line.
 */
export function splitMemDefnComment(s) {
    const matchValue = s.indexOf("//") | 0;
    if (matchValue === -1) {
        return [s, undefined];
    }
    else {
        const i = matchValue | 0;
        const comment = StringModule_Trim(substring(s, i + 2));
        return [substring(s, 0, i), (comment === "") ? undefined : comment];
    }
}

/**
 * Parse one "address data" line of a .ram file, which may carry a comment. lineNo is the 1-based
 * line number in the file, used only to say where an error is.
 */
export function readMemDefnLine(addressWidth, wordWidth, lineNo, s) {
    const patternInput = splitMemDefnComment(s);
    const nums = StringModule_SplitRemoveEmptyEntries([" ", "\t", ",", ";", ":", "\""], patternInput[0]);
    if (!equalsWith((x, y) => (x === y), nums, defaultOf()) && (nums.length === 2)) {
        const data = item_1(1, nums);
        const addr = item_1(0, nums);
        const addrNum = strToIntCheckWidth(addressWidth, addr);
        const dataNum = strToIntCheckWidth(wordWidth, data);
        const copyOfStruct = addrNum;
        if (copyOfStruct.tag === 1) {
            return new FSharpResult$2(/* Error */ 1, [`Line ${lineNo}: '${s}' has an invalid address (${addr}). ${copyOfStruct.fields[0]}`]);
        }
        else {
            const copyOfStruct_1 = dataNum;
            if (copyOfStruct_1.tag === 1) {
                return new FSharpResult$2(/* Error */ 1, [`Line ${lineNo}: '${s}' has an invalid data item (${data}). ${copyOfStruct_1.fields[0]}`]);
            }
            else {
                return new FSharpResult$2(/* Ok */ 0, [[copyOfStruct.fields[0], copyOfStruct_1.fields[0], patternInput[1]]]);
            }
        }
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [`Line ${lineNo}: '${s}' has ${nums.length} items: valid lines consist of two numbers`]);
    }
}

/**
 * Parse the lines of a .ram file, reporting the first bad line. lines must be the file's
 * lines as they are, blanks included, so that reported line numbers match the file.
 */
export function readMemLines(addressWidth, wordWidth, lines) {
    let array_1;
    const parse = map_1((tupledArg_1) => readMemDefnLine(addressWidth, wordWidth, tupledArg_1[0], tupledArg_1[1]), (array_1 = mapIndexed((i, line) => [i + 1, StringModule_Trim(line)], lines), array_1.filter((tupledArg) => (StringModule_Trim(splitMemDefnComment(tupledArg[1])[0]) !== ""))));
    const matchValue = tryFind((_arg_1) => (_arg_1.tag === 1), parse);
    if (matchValue != null) {
        const copyOfStruct = matchValue;
        if (copyOfStruct.tag === 1) {
            return new FSharpResult$2(/* Error */ 1, [copyOfStruct.fields[0]]);
        }
        else {
            return toFail(printf("What? can\'t happen"));
        }
    }
    else {
        const defs = map_1((_arg_2) => ((_arg_2.tag === 0) ? _arg_2.fields[0] : toFail(printf("What?"))), parse);
        let repeats;
        const array_3 = Array_groupBy((tupledArg_2) => tupledArg_2[0], defs, {
            Equals: equals,
            GetHashCode: (x_1) => (bigintHash(x_1) | 0),
        });
        repeats = array_3.filter((tupledArg_3) => (tupledArg_3[1].length > 1));
        if (!equalsWith(equalArrays, repeats, [])) {
            return new FSharpResult$2(/* Error */ 1, [`Memory addresses ${interpolate("%A%P()", [map_1((tuple) => tuple[0], repeats)])} are repeated`]);
        }
        else {
            return new FSharpResult$2(/* Ok */ 0, [defs]);
        }
    }
}

/**
 * The locations defined by a .ram file, and the comments written against them. Locations with no
 * comment are absent from the second map.
 */
export function readMemDefns(addressWidth, wordWidth, fPath) {
    return Result_Bind((arg_1) => Result_Map((defs) => [ofArray_1(map_1((tupledArg) => [tupledArg[0], tupledArg[1]], defs), {
        Compare: (x, y) => (compare(x, y) | 0),
    }), ofArray_1(choose((tupledArg_1) => {
        const option_1 = tupledArg_1[2];
        if (option_1 != null) {
            return [tupledArg_1[0], option_1];
        }
        else {
            return undefined;
        }
    }, defs), {
        Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
    })], readMemLines(addressWidth, wordWidth, split(arg_1, ["\n"], undefined, 0))), tryReadFileSync(fPath));
}

export function writeMemDefns(fPath, mem) {
    try {
        const comments = defaultArg(mem.Comments, empty_1({
            Compare: (x, y) => (compare(x, y) | 0),
        }));
        return new FSharpResult$2(/* Ok */ 0, [writeFile(fPath, StringModule_Concat("\n", map_1((tupledArg) => {
            const a = tupledArg[0];
            const defn = concat(hexBignum(a), "\t", hexBignum(tupledArg[1]));
            const matchValue = tryFind_1(a, comments);
            if (matchValue == null) {
                return defn;
            }
            else {
                return concat(defn, "\t// ", matchValue);
            }
        }, sortBy_1((tuple) => tuple[0], toArray_1(mem.Data), {
            Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
        }))))]);
    }
    catch (e) {
        return new FSharpResult$2(/* Error */ 1, [concat("Error writing file \'", fPath, "\': ", e.message)]);
    }
}

/**
 * Return data for memory if it is linked to a ram.
 * Return mem data if it is unlinked
 * Error if the read fails ot the file parse fails.
 */
export function initialiseMem(mem, projectPath) {
    let matchValue;
    return Result_Map((tupledArg) => {
        const comments = tupledArg[1];
        return new Memory1(mem.Init, mem.AddressWidth, mem.WordWidth, tupledArg[0], isEmpty_2(comments) ? undefined : comments);
    }, (matchValue = mem.Init, (matchValue.tag === 1) ? readMemDefns(mem.AddressWidth, mem.WordWidth, pathJoin([projectPath, matchValue.fields[0] + ".ram"])) : ((matchValue.tag === 0) ? (new FSharpResult$2(/* Ok */ 0, [[mem.Data, defaultArg(mem.Comments, empty_1({
        Compare: (x, y) => (compare(x, y) | 0),
    }))]])) : (new FSharpResult$2(/* Error */ 1, [`Unsupported legacy memory type '${mem.Init}'`])))));
}

/**
 * Save state to normal file. Automatically add the .dgm suffix.
 * This version will not correctly deal with bigint numbers.
 * See svaStateToFileNew
 * If serialisation fails the existing file is left untouched and an Error returned.
 */
export function saveStateToFile(folderPath, baseName_1, state_, state__1, state__2) {
    const state = [state_, state__1, state__2];
    const path = pathJoin([folderPath, baseName_1 + ".dgm"]);
    return Result_Bind((data) => writeFile(path, data), JsonHelpers_stateToJsonString(state[0], state[1], state[2]));
}

/**
 * Save state to file. Automatically add the .dgm suffix.
 * This is the new version of the function that uses the new state format and copes with bigints
 * However, it seems that it is not used??
 */
export function saveStateToFileExperimental(folderPath, baseName_1, state_, state__1, state__2) {
    const state = [state_, state__1, state__2];
    const path = pathJoin([folderPath, baseName_1 + ".dgmNew"]);
    return Result_Bind((data) => writeFile(path, data), JsonHelpers_stateToJsonStringExperimental(state[0], state[1], state[2]));
}

/**
 * Create new empty diagram file. Automatically add the .dgm suffix.
 */
export function createEmptyDgmFile(folderPath, baseName_1) {
    return saveStateToFile(folderPath, baseName_1, [empty(), empty()], undefined, new SheetInfo(CCForm.User, undefined, undefined, undefined, undefined));
}

export function stripVertices(conn) {
    return new LegacyCanvas_LegacyConnection(conn.Id, conn.Source, conn.Target, empty());
}

export function magnifySheet(magnification, comp) {
    return new LegacyCanvas_LegacyComponent(comp.Id, comp.Type, comp.Label, comp.InputPorts, comp.OutputPorts, magnification * (comp.X + (comp.W / 2)), magnification * (comp.Y + (comp.H / 2)), -1, -1);
}

/**
 * Update from old component types to new
 * In addition do some sanity checks
 * The standard way to add functionality to an existing component is to create a new
 * component type, keeping the old type. Then on reading sheets from disk both new and old
 * will be correctly read. This function will be called on load and will convert from the old
 * type to the new one so that the rest of issie need only process new types, but compatibility
 * with saved old types remains.
 */
export function getLatestComp(comp) {
    const updateMem = (mem) => (new Memory1(InitMemData.FromData, mem.AddressWidth, mem.WordWidth, mem.Data, undefined));
    const matchValue = comp.Type;
    switch (matchValue.tag) {
        case 45:
            return new Component(comp.Id, new ComponentType(/* RAM1 */ 41, [updateMem(matchValue.fields[0])]), comp.Label, comp.InputPorts, comp.OutputPorts, comp.X, comp.Y, comp.H, comp.W, comp.SymbolInfo, comp.SlotInfo);
        case 44:
            return new Component(comp.Id, new ComponentType(/* ROM1 */ 40, [updateMem(matchValue.fields[0])]), comp.Label, comp.InputPorts, comp.OutputPorts, comp.X, comp.Y, comp.H, comp.W, comp.SymbolInfo, comp.SlotInfo);
        case 43:
            return new Component(comp.Id, new ComponentType(/* AsyncROM1 */ 39, [updateMem(matchValue.fields[0])]), comp.Label, comp.InputPorts, comp.OutputPorts, comp.X, comp.Y, comp.H, comp.W, comp.SymbolInfo, comp.SlotInfo);
        case 49: {
            const cVal = matchValue.fields[1];
            return new Component(comp.Id, new ComponentType(/* Constant1 */ 7, [matchValue.fields[0], cVal, `${interpolate("%A%P()", [cVal])}`]), comp.Label, comp.InputPorts, comp.OutputPorts, comp.X, comp.Y, comp.H, comp.W, comp.SymbolInfo, comp.SlotInfo);
        }
        case 48:
            return new Component(comp.Id, new ComponentType(/* Input1 */ 0, [matchValue.fields[0], undefined]), comp.Label, comp.InputPorts, comp.OutputPorts, comp.X, comp.Y, comp.H, comp.W, comp.SymbolInfo, comp.SlotInfo);
        default:
            return comp;
    }
}

function getLatestJsonCanvas(state) {
    const stripConns = (canvas) => {
        const noVertexConns = map(stripVertices, canvas[1]);
        const tupledArg = [map((comp) => magnifySheet(1.25, comp), canvas[0]), noVertexConns];
        return legacyTypesConvert(tupledArg[0], tupledArg[1]);
    };
    switch (state.tag) {
        case 1:
            return stripConns(state.fields[0]);
        case 2: {
            const canvas_3 = state.fields[0];
            return legacyTypesConvert(canvas_3[0], canvas_3[1]);
        }
        case 3:
            return state.fields[0];
        case 4:
            return state.fields[0];
        default:
            return stripConns(state.fields[0]);
    }
}

/**
 * The file's canvas as in-memory types: ids become integers here (Helpers.sheetOfJson).
 */
export function getLatestCanvas(state) {
    let patternInput;
    const tupledArg = getLatestJsonCanvas(state);
    patternInput = sheetOfJson(tupledArg[0], tupledArg[1], undefined, undefined);
    return [map(getLatestComp, patternInput[0][0]), patternInput[0][1]];
}

/**
 * If the component is a RAM update its contents based on its initialiser
 */
export function checkMemoryContents(projectPath, comp) {
    const matchValue = comp.Type;
    let matchResult, mem_4;
    switch (matchValue.tag) {
        case 41: {
            if (!StringModule_EndsWith("backup", StringModule_ToLower(projectPath))) {
                matchResult = 0;
                mem_4 = matchValue.fields[0];
            }
            else {
                matchResult = 1;
            }
            break;
        }
        case 40: {
            if (!StringModule_EndsWith("backup", StringModule_ToLower(projectPath))) {
                matchResult = 0;
                mem_4 = matchValue.fields[0];
            }
            else {
                matchResult = 1;
            }
            break;
        }
        case 39: {
            if (!StringModule_EndsWith("backup", StringModule_ToLower(projectPath))) {
                matchResult = 0;
                mem_4 = matchValue.fields[0];
            }
            else {
                matchResult = 1;
            }
            break;
        }
        case 42: {
            if (!StringModule_EndsWith("backup", StringModule_ToLower(projectPath))) {
                matchResult = 0;
                mem_4 = matchValue.fields[0];
            }
            else {
                matchResult = 1;
            }
            break;
        }
        default:
            matchResult = 1;
    }
    switch (matchResult) {
        case 0: {
            const matchValue_1 = mem_4.Init;
            if (matchValue_1.tag === 1) {
                const fPath = pathJoin([projectPath, matchValue_1.fields[0] + ".ram"]);
                const memData = readMemDefns(mem_4.AddressWidth, mem_4.WordWidth, fPath);
                if (memData.tag === 1) {
                    error_1(`reloading component ${comp.Label} from its file ${fPath}: ${memData.fields[0]}`);
                    return comp;
                }
                else {
                    const memDat = memData.fields[0][0];
                    const comments = memData.fields[0][1];
                    if (!memDat.Equals(mem_4.Data)) {
                        warn(`RAM file ${fPath} has changed, so component ${comp.Label} is now different`);
                    }
                    const mem_5 = new Memory1(mem_4.Init, mem_4.AddressWidth, mem_4.WordWidth, memDat, isEmpty_2(comments) ? undefined : comments);
                    return new Component(comp.Id, getMemType(comp.Type)(mem_5), comp.Label, comp.InputPorts, comp.OutputPorts, comp.X, comp.Y, comp.H, comp.W, comp.SymbolInfo, comp.SlotInfo);
                }
            }
            else {
                return comp;
            }
        }
        default:
            return comp;
    }
}

/**
 * load a component from its canvas and other elements
 */
export function makeLoadedComponentFromCanvasData(canvas_, canvas__1, filePath, timeStamp, waveInfo, sheetInfo) {
    let option_1, option_3, option_5, option_7, option_10;
    const canvas = [canvas_, canvas__1];
    const projectPath = dirName(filePath);
    const patternInput = parseDiagramSignatureFor((option_1 = sheetInfo, (option_1 != null) ? option_1.ArrayInfo : undefined), (option_3 = sheetInfo, (option_3 != null) ? option_3.ParameterDefinitions : undefined), canvas[0], canvas[1]);
    const comps = canvas[0];
    const comps$0027 = map((comp) => checkMemoryContents(projectPath, comp), comps);
    const ramChanges = map((tuple) => tuple[0], filter_1((tupledArg) => !equals_1(tupledArg[0].Type, tupledArg[1].Type), zip(comps$0027, comps)));
    let patternInput_1;
    if (sheetInfo != null) {
        const sI_2 = sheetInfo;
        patternInput_1 = [sI_2.Form, sI_2.Description];
    }
    else {
        patternInput_1 = [CCForm.User, undefined];
    }
    return [new LoadedComponent(getBaseNameNoExtension(filePath), timeStamp, filePath, waveInfo, [comps$0027, canvas[1]], patternInput[0], patternInput[1], (option_5 = sheetInfo, (option_5 != null) ? option_5.ParameterDefinitions : undefined), patternInput_1[0], false, patternInput_1[1], defaultArg((option_7 = sheetInfo, (option_7 != null) ? option_7.IsTopSheet : undefined), false), (option_10 = sheetInfo, (option_10 != null) ? option_10.ArrayInfo : undefined)), ramChanges];
}

/**
 * Make a loadedComponent from the file read from filePath.
 * Return the component, or an Error string.
 * Build a sheet from the TEXT of a .dgm, naming it after the path it is to be saved back to.
 * 
 * Separate from tryLoadComponentFromPath because a .dgm's text does not only ever come from a
 * .dgm: a library component holds one inside an .ldgm, and materialising one writes that text out
 * before loading it. This is the whole of the decode, and the path is used only for the sheet's
 * name and FilePath - nothing here reads it.
 */
export function tryLoadComponentFromText(filePath, contents) {
    let ldc, arg;
    const matchValue = JsonHelpers_jsonStringToState(contents);
    if (matchValue.tag === 0) {
        const state = matchValue.fields[0];
        const jsonCanvas = getLatestJsonCanvas(state);
        const patternInput = sheetOfJson(jsonCanvas[0], jsonCanvas[1], JsonHelpers_SavedInfo__get_getWaveInfo(state), JsonHelpers_SavedInfo__get_getSheetInfo(state));
        const canvas = [map(getLatestComp, patternInput[0][0]), patternInput[0][1]];
        return new FSharpResult$2(/* Ok */ 0, [(ldc = makeLoadedComponentFromCanvasData(canvas[0], canvas[1], filePath, JsonHelpers_SavedInfo__get_getTimeStamp(state), patternInput[1], patternInput[2])[0], new LoadedComponent(ldc.Name, ldc.TimeStamp, ldc.FilePath, ldc.WaveInfo, ldc.CanvasState, ldc.InputLabels, ldc.OutputLabels, ldc.LCParameterSlots, ldc.Form, jsonCanvasHasOldIds(jsonCanvas[0], jsonCanvas[1]), ldc.Description, ldc.IsTopSheet, ldc.ArrayInfo))]);
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [(arg = getBaseNameNoExtension(filePath), toText(printf("Can\'t load component %s because of Error: could not convert file \'%s\' to a                           valid issie design sheet. Details: %s"))(arg)(filePath)(matchValue.fields[0]))]);
    }
}

export function tryLoadComponentFromPath(filePath) {
    if (!exists(filePath)) {
        return new FSharpResult$2(/* Error */ 1, [toText(printf("Can\'t read file from %s because it does not seem to exist!"))(filePath)]);
    }
    else {
        return Result_Bind((contents) => tryLoadComponentFromText(filePath, contents), (() => {
            try {
                return new FSharpResult$2(/* Ok */ 0, [readFile(filePath)]);
            }
            catch (e) {
                return new FSharpResult$2(/* Error */ 1, [`Error ${e.message} reading file '${filePath}'`]);
            }
        })());
    }
}

export class LoadStatus extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Resolve", "OkComp", "OkAuto"];
    }
}

export function LoadStatus_$reflection() {
    return union_type("FilesIO.LoadStatus", [], LoadStatus, () => [[["Item1", LoadedComponent_$reflection()], ["Item2", LoadedComponent_$reflection()]], [["Item", LoadedComponent_$reflection()]], [["Item", LoadedComponent_$reflection()]]]);
}

function isDemoProjectPath(path) {
    return replace(path, "\\", "/").indexOf("/demos/") >= 0;
}

/**
 * load all files in folderpath. Return Ok list of LoadStatus or a single Error.
 */
export function loadAllComponentFiles(folderPath) {
    let x;
    try {
        x = (new FSharpResult$2(/* Ok */ 0, [readdir(folderPath)]));
    }
    catch (e) {
        x = (new FSharpResult$2(/* Error */ 1, [toText(printf("Error reading Issie project directory at \'%s: %A"))(folderPath)(e)]));
    }
    if (x.tag === 0) {
        return ResultList_sequence(map((fileName) => {
            if (fileNameIsBad(pathWithoutExtension(fileName).split(""))) {
                return new FSharpResult$2(/* Error */ 1, [toText(printf("Can\'t load file name \'%s\' from project \'%s\' because it contains incorrect characters.\\n \\\r\n                    File names used as sheets must contain only alphanumeric and space characters before the \'.dgm\' extension"))(fileName)(folderPath)]);
            }
            else {
                const filePath_1 = pathJoin([folderPath, fileName]);
                const ldComp = tryLoadComponentFromPath(filePath_1);
                const autoComp = isDemoProjectPath(folderPath) ? (new FSharpResult$2(/* Error */ 1, ["Demo autosave disabled"])) : tryLoadComponentFromPath(filePath_1 + "auto");
                dbg(128, concat("loaded ", fileName));
                let matchResult, autoComp_2, ldComp_2, ldComp_3, autoComp_3, msg_1;
                const copyOfStruct = ldComp;
                if (copyOfStruct.tag === 1) {
                    const copyOfStruct_1 = autoComp;
                    if (copyOfStruct_1.tag === 0) {
                        matchResult = 2;
                        autoComp_3 = copyOfStruct_1.fields[0];
                    }
                    else {
                        matchResult = 3;
                        msg_1 = copyOfStruct.fields[0];
                    }
                }
                else {
                    const copyOfStruct_2 = autoComp;
                    if (copyOfStruct_2.tag === 0) {
                        if (compare_1(copyOfStruct.fields[0].TimeStamp, copyOfStruct_2.fields[0].TimeStamp) < 0) {
                            matchResult = 0;
                            autoComp_2 = copyOfStruct_2.fields[0];
                            ldComp_2 = copyOfStruct.fields[0];
                        }
                        else {
                            matchResult = 1;
                            ldComp_3 = copyOfStruct.fields[0];
                        }
                    }
                    else {
                        matchResult = 1;
                        ldComp_3 = copyOfStruct.fields[0];
                    }
                }
                switch (matchResult) {
                    case 0:
                        return new FSharpResult$2(/* Ok */ 0, [new LoadStatus(/* Resolve */ 0, [ldComp_2, autoComp_2])]);
                    case 1:
                        return new FSharpResult$2(/* Ok */ 0, [new LoadStatus(/* OkComp */ 1, [ldComp_3])]);
                    case 2:
                        return new FSharpResult$2(/* Ok */ 0, [new LoadStatus(/* OkAuto */ 2, [autoComp_3])]);
                    default:
                        return new FSharpResult$2(/* Error */ 1, [msg_1]);
                }
            }
        }, filter_1((arg_2) => (".dgm" === extName(arg_2)), toList(x.fields[0]))));
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [x.fields[0]]);
    }
}

/**
 * Ask the user a new project path, with a dialog window.
 * Return None if the user exits withouth selecting a path.
 */
export function askForNewFile(projectPath) {
    let collection;
    const options = {};
    options.filters = ((collection = makeFileFilters("Memory Contents File", "ram"), Array.from(collection)));
    options.defaultPath = projectPath;
    options.title = "Enter new file name";
    options.nameFieldLabel = "New file name";
    options.buttonLabel = "Save memory content to file";
    options.properties = ["showOverwriteConfirmation"];
    const matchValue = window.issieBridge.dialog.save(options);
    if (matchValue === "") {
        return undefined;
    }
    else {
        return matchValue;
    }
}

export function saveAllProjectFilesFromLoadedComponentsToDisk(proj) {
    iterate((ldc) => {
        const name = ldc.Name;
        saveStateToFile(proj.ProjectPath, name, ldc.CanvasState, ldc.WaveInfo, new SheetInfo(ldc.Form, ldc.Description, ldc.LCParameterSlots, ldc.IsTopSheet, ldc.ArrayInfo));
        const pr = persistFileToExternalStorageAsync(pathJoin([proj.ProjectPath, name + ".dgm"]));
        void pr;
        removeFileWithExtn(".dgmauto", proj.ProjectPath, name);
    }, proj.LoadedComponents);
}

export function openWriteDialogAndWriteMemory(mem, path) {
    const matchValue = askForNewFile(path);
    if (matchValue != null) {
        const fpath = matchValue;
        const fpath$0027 = !StringModule_Contains(".", fpath) ? (fpath + ".ram") : fpath;
        writeMemDefns(fpath$0027, mem);
        return fpath$0027;
    }
    else {
        return undefined;
    }
}

